import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FlameIcon, TrendingUpIcon, ClockIcon, PlusIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CategoryNav } from '../components/CategoryNav';
import { RantCard } from '../components/RantCard';
import { Rant, RantCategory, SortOption } from '../types';
import { getRantsFromDatabase } from '../services/rantService';
import { supabase } from '../config/supabaseClient';
export const HallOfRagePage: React.FC = () => {
  console.log('🎯 HallOfRagePage component rendering');
  const [rants, setRants] = useState<Rant[]>([]);
  const [activeCategory, setActiveCategory] = useState<RantCategory>('all');
  const [sortOption, setSortOption] = useState<SortOption>('most-votes');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  useEffect(() => {
    console.log('🔄 HallOfRagePage useEffect running');
    loadRants();
    window.scrollTo(0, 0);
    // Set up real-time subscription
    const channel = supabase.channel('rants-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'rants'
    }, () => {
      console.log('📡 Real-time update received, reloading rants');
      loadRants();
    }).subscribe();
    return () => {
      console.log('🧹 HallOfRagePage cleanup');
      supabase.removeChannel(channel);
    };
  }, []);
  const loadRants = async () => {
    console.log('📥 loadRants() called');
    setIsLoading(true);
    const loadedRants = await getRantsFromDatabase();
    console.log('📦 Loaded rants:', loadedRants.length);
    setRants(loadedRants);
    setIsLoading(false);
  };
  // Handle inline updates from RantCard
  const handleRantUpdated = (rantId: string, updates: Partial<Rant>) => {
    setRants(prevRants => prevRants.map(rant => rant.id === rantId ? {
      ...rant,
      ...updates
    } : rant));
  };
  const handleCategoryChange = (category: RantCategory) => {
    setActiveCategory(category);
  };
  const handleSortChange = (option: SortOption) => {
    setSortOption(option);
  };
  // Filter rants by category
  const filteredRants = rants.filter(rant => activeCategory === 'all' ? true : rant.category === activeCategory);
  // Sort rants based on selected option
  const sortedRants = [...filteredRants].sort((a, b) => {
    switch (sortOption) {
      case 'most-votes':
        return b.votes - a.votes;
      case 'newest':
        return b.timestamp - a.timestamp;
      case 'most-relatable':
        // For "most-relatable", we'll use a combination of votes and recency
        const aScore = a.votes * 0.7 + 1 / (Date.now() - a.timestamp) * 0.3;
        const bScore = b.votes * 0.7 + 1 / (Date.now() - b.timestamp) * 0.3;
        return bScore - aScore;
      default:
        return 0;
    }
  });
  // Get current date minus 7 days for "Rant of the Week" badge
  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  // Find rant with most votes in the past week
  const rantOfWeek = rants.filter(rant => rant.timestamp > oneWeekAgo).sort((a, b) => b.votes - a.votes)[0];
  return <div className="min-h-screen flex flex-col bg-background" data-id="element-144">
      <Helmet data-id="element-145">
        <title data-id="element-146">Hall of Rage - IBD Rant</title>
        <meta name="description" content="The IBD Hall of Rage - Read and fire up the rants that resonate. See what the IBD community is frustrated about and know you're not alone." data-id="element-147" />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" data-id="element-148" />
        <meta property="og:title" content="Hall of Rage - IBD Rant" data-id="element-149" />
        <meta property="og:description" content="The IBD Hall of Rage - Read and fire up the rants that resonate. See what the IBD community is frustrated about and know you're not alone." data-id="element-150" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-151" />
        <meta name="twitter:title" content="Hall of Rage - IBD Rant" data-id="element-152" />
        <meta name="twitter:description" content="The IBD Hall of Rage - Read and fire up the rants that resonate. See what the IBD community is frustrated about and know you're not alone." data-id="element-153" />
      </Helmet>
      <Header data-id="element-154" />
      <main className="flex-grow py-12 px-4 bg-background" data-id="element-155">
        <div className="max-w-4xl mx-auto" data-id="element-156">
          <motion.div className="text-center mb-12" initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-157">
            <motion.h1 className="text-3xl md:text-4xl font-bold mb-2 flex items-center justify-center gap-2" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} data-id="element-158">
              <FlameIcon className="h-8 w-8 text-primary" data-id="element-159" />
              The IBD Hall of Rage
            </motion.h1>
            <motion.p className="text-lg text-text-secondary" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} data-id="element-160">
              What We're All Mad About
            </motion.p>
          </motion.div>
          <CategoryNav activeCategory={activeCategory} onCategoryChange={handleCategoryChange} data-id="element-161" />
          <div className="flex justify-end mb-6" data-id="element-162">
            <div className="flex bg-background-light rounded-lg overflow-hidden" data-id="element-163">
              <button onClick={() => handleSortChange('most-votes')} className={`px-4 py-2 flex items-center gap-1 text-sm ${sortOption === 'most-votes' ? 'bg-primary text-white' : 'hover:bg-background'}`} data-id="element-164">
                <FlameIcon className="h-4 w-4" data-id="element-165" />
                Most Fired Up
              </button>
              <button onClick={() => handleSortChange('newest')} className={`px-4 py-2 flex items-center gap-1 text-sm ${sortOption === 'newest' ? 'bg-primary text-white' : 'hover:bg-background'}`} data-id="element-166">
                <ClockIcon className="h-4 w-4" data-id="element-167" />
                Fresh Rage
              </button>
              <button onClick={() => handleSortChange('most-relatable')} className={`px-4 py-2 flex items-center gap-1 text-sm ${sortOption === 'most-relatable' ? 'bg-primary text-white' : 'hover:bg-background'}`} data-id="element-168">
                <TrendingUpIcon className="h-4 w-4" data-id="element-169" />
                Most Relatable
              </button>
            </div>
          </div>
          {isLoading ? <div className="text-center py-12" data-id="element-170">
              <motion.div className="inline-block" animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-171">
                <FlameIcon className="h-12 w-12 text-primary" data-id="element-172" />
              </motion.div>
              <p className="text-text-secondary mt-4" data-id="element-173">Loading rants...</p>
            </div> : sortedRants.length === 0 ? <div className="text-center py-12" data-id="element-174">
              <p className="text-text-secondary text-lg" data-id="element-175">
                No rants in this category yet. Be the first to vent!
              </p>
            </div> : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6,
          duration: 0.5
        }} data-id="element-176">
              {sortedRants.map(rant => <RantCard key={rant.id} rant={rant} isRantOfWeek={rantOfWeek && rant.id === rantOfWeek.id} isSpicyTake={rant.votes >= 10 && /worst|hate|terrible|awful|ridiculous|absurd/i.test(rant.text)} isUniversalTruth={rant.votes >= 50} onRantUpdated={handleRantUpdated} data-id="element-177" />)}
            </motion.div>}
        </div>
      </main>
      {/* Floating Action Button */}
      <Link to="/" data-id="element-178">
        <motion.button className="fixed bottom-8 right-8 bg-primary hover:bg-primary-hover text-white rounded-full p-4 shadow-2xl flex items-center gap-2 font-bold z-50" initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: 1,
        type: 'spring',
        stiffness: 260,
        damping: 20
      }} whileHover={{
        scale: 1.1,
        boxShadow: '0 0 30px rgba(255, 69, 0, 0.5)'
      }} whileTap={{
        scale: 0.95
      }} data-id="element-179">
          <PlusIcon className="h-6 w-6" data-id="element-180" />
          <span className="pr-2" data-id="element-181">Add Your Rant</span>
        </motion.button>
      </Link>
      <Footer data-id="element-182" />
    </div>;
};